import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import GenericModal from '../../../components/modal/GenericModal';
import RubricaUpload from './RubricaUpload';
import { downloadFile } from '../../../consumers/backendConsumer';
import { AuthContext } from '../../../hooks/context/authContext';
import { useGetRequest } from '../../../hooks/useGetRequest/useGetRequest';
import { usePostRequest } from '../../../hooks/usePostRequest/usePostRequest';
import {
    actualizarClaseModeloAPI,
    listarRubricasClaseModeloAPI,
    listarRubricasEvaluadorClaseModeloAPI,
    guardarRubricaEvaluadorClaseModeloAPI,
} from '../../../consumers/backendApisUrls';

const ESTADO_ENVIADO = 18;
const valor = (...valores) => valores.find(v => v !== undefined && v !== null && v !== '') ?? '';
const data = response => response?.data?.Data ?? response?.data?.data ?? response?.data ?? [];

// ──────────────────────────────────────────────────────────────────────────
// "Completar clase modelo" (28/09/2026, a pedido de Jody): el evaluador
// asignado (TP_ClaseModelo.IdEmpleadoEvaluador) sube/reemplaza la rúbrica de
// evaluación y registra un comentario. Se abre desde "Clases modelo por
// completar" en Mis Traslados y Promociones (ClasesModeloPorCompletar.jsx),
// sobre una fila de GetClasesModeloPorEvaluar — que ya trae todos los datos
// de cabecera, así que este modal no necesita pedir un detalle aparte (mismo
// razonamiento que CompletarEntrevistaModal.jsx con GetEntrevistasPorEvaluar).
//
// Jody pidió explícitamente "crea un modal similar a este" adjuntando una
// captura de ResultadoClaseModelo.jsx ("Resultado Clase modelo Líder", el
// modal que usa BandejaPostulantes.jsx para el Director de Colegio): mismo
// layout (datos del postulante, datos de programación, rúbrica con
// "Reemplazar rúbrica", comentario opcional, botón Enviar) y las mismas 3
// APIs de rúbrica/envío (listarRubricasClaseModeloAPI / guardarRubricaClaseModeloAPI
// / actualizarClaseModeloAPI con IdEstadoPostulacion=18/Enviado — igual que
// ese modal). Es un componente NUEVO, no una reutilización directa de
// ResultadoClaseModelo.jsx, por una diferencia de control de acceso:
//   - ResultadoClaseModelo.jsx gatea la vista por ROL_DIRECTOR_COLEGIO (rol
//     fijo), porque BandejaPostulantes.jsx lista TODOS los postulantes y solo
//     el Director de Colegio debe poder evaluar ahí.
//   - Acá NO hace falta ese gate: GetClasesModeloPorEvaluar ya filtra
//     server-side por IdEmpleadoEvaluador = empleado logueado, así que
//     cualquiera que vea esta fila ya es, por definición, el evaluador
//     asignado — sin importar su rol.
//
// Fuente de los campos de cabecera: GET_CLASES_MODELO_POR_EVALUAR (Java) fue
// extendida (28/09/2026) para traer también IdPostulante, Dni, SedeEvaluadora
// y SedeOrigen, además de las 5 columnas que ya usa la lista (NombrePostulante,
// NombreEvaluador, CargoPostulante, SedePostulante, FechaLimite). Ver
// Sql.PostulanteSQL.GET_CLASES_MODELO_POR_EVALUAR.
//
// Nota: "Enviar" reutiliza ActualizarClaseModelo tal cual lo usa
// ResultadoClaseModelo.jsx (mismo payload, mismo IdEstadoPostulacion=18). No
// está 100% confirmado con Jody que ese estado/transición sea el semánticamente
// correcto para ESTE flujo (evaluador cualquiera, no necesariamente Director de
// Colegio) — se asume que sí porque es la única acción de "guardar evaluación
// de clase modelo" que existe hoy en el backend; avisar si el comportamiento
// esperado en producción es otro (por ejemplo si debería quitar la fila de
// "Clases modelo por completar" con otro criterio).
// ──────────────────────────────────────────────────────────────────────────

const CompletarClaseModeloModal = ({ toggleModal, refreshPage, claseModelo = {} }) => {
    const { state } = useContext(AuthContext);
    const [executeGet] = useGetRequest();
    const [executePost] = usePostRequest();
    const [comentario, setComentario] = useState('');
    const [rubricasPlantilla, setRubricasPlantilla] = useState([]);
    const [rubricasEvaluador, setRubricasEvaluador] = useState([]);
    const [subiendo, setSubiendo] = useState(false);
    const [enviando, setEnviando] = useState(false);

    const idPostulante = Number(valor(claseModelo.IdPostulante, claseModelo.idPostulante, 0));
    const idClaseModelo = Number(valor(claseModelo.IdClaseModelo, claseModelo.idClaseModelo, 0));
    const nombre = valor(claseModelo.NombrePostulante, '-');
    const dni = valor(claseModelo.Dni, '-');
    const puesto = valor(claseModelo.CargoPostulante, '-');

    const cargarRubricas = async () => {
        if (!idPostulante) return;

        const responsePlantilla = await executeGet(listarRubricasClaseModeloAPI(idPostulante));
        const rowsPlantilla = data(responsePlantilla);

        setRubricasPlantilla(Array.isArray(rowsPlantilla) ? rowsPlantilla : []);

        const responseEvaluador = await executeGet(listarRubricasEvaluadorClaseModeloAPI(idPostulante));
        const rowsEvaluador = data(responseEvaluador);

        setRubricasEvaluador(Array.isArray(rowsEvaluador) ? rowsEvaluador : []);
    };

    useEffect(() => {
        cargarRubricas();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idPostulante]);

    const guardarRubrica = async file => {
        if (!file || !idPostulante) return;

        setSubiendo(true);
        try {
            await executePost(guardarRubricaEvaluadorClaseModeloAPI(idPostulante), { File: file });
            await cargarRubricas();
            toast.success('Rúbrica cargada correctamente.');
        } finally {
            setSubiendo(false);
        }
    };

    const descargar = archivo => {
        downloadFile(state.token, archivo.Path ?? archivo.path, archivo.OriginalFileName ?? archivo.originalFileName);
    };

    const enviar = async () => {
        if (!idPostulante || !idClaseModelo) {
            toast.error('No se encontró la programación de Clase Modelo a actualizar.');
            return;
        }
        if (!rubricasEvaluador.length) {
            toast.warning('Debes cargar la rúbrica de evaluación antes de enviar.');
            return;
        }

        setEnviando(true);
        try {
            const response = await executePost(actualizarClaseModeloAPI(), {
                idClaseModelo,
                idPostulante,
                comentarioEvaluador: comentario.trim() || null,
                motivoObservado: null,
                idEstadoPostulacion: ESTADO_ENVIADO,
            });
            if (response?.response?.status >= 400) {
                toast.error(response?.response?.data?.Message ?? response?.response?.data?.message ?? 'No se pudo enviar la evaluación.');
                return;
            }
            toast.success('Clase modelo completada correctamente.');
            refreshPage && await refreshPage();
            toggleModal && toggleModal();
        } finally {
            setEnviando(false);
        }
    };

    const ultimaRubricaPlantilla = rubricasPlantilla[0];
    const ultimaRubricaEvaluador = rubricasEvaluador[0];

    return (
        <GenericModal showModal={toggleModal} titulo="Completar clase modelo" width="900px">
            <Container>
                <Grid>
                    <Field><label>Nombre del candidato</label><ReadOnly>{nombre}</ReadOnly></Field>
                    <Field><label>DNI</label><ReadOnly>{dni}</ReadOnly></Field>
                    <Field><label>Puesto actual</label><ReadOnly>{puesto}</ReadOnly></Field>
                </Grid>

                <Divider />
                <SectionTitle>Datos de programación</SectionTitle>
                <Grid>
                    <Field><label>Sede evaluadora</label><ReadOnly>{valor(claseModelo.SedeEvaluadora, '-')}</ReadOnly></Field>
                    <Field><label>Responsable evaluador</label><ReadOnly>{valor(claseModelo.NombreEvaluador, '-')}</ReadOnly></Field>
                    <Field><label>Sede de origen</label><ReadOnly>{valor(claseModelo.SedeOrigen, '-')}</ReadOnly></Field>
                    <Field><label>Fecha límite</label><ReadOnly>{valor(claseModelo.FechaLimite, '-')}</ReadOnly></Field>
                </Grid>

                <Divider />
                <SectionTitle>Rúbrica de evaluación</SectionTitle>

                <RubricaUpload
                    id="clase-modelo-rubrica-evaluador-mistrasladosypromociones"
                    layout="column"
                    archivos={[
                        ultimaRubricaPlantilla && { archivo: ultimaRubricaPlantilla, etiqueta: 'Plantilla GDH' },
                        ultimaRubricaEvaluador && { archivo: ultimaRubricaEvaluador, etiqueta: 'Rúbrica del evaluador' },
                    ].filter(Boolean)}
                    onDescargar={descargar}
                    uploadFileToS3={guardarRubrica}
                    textButton="Subir mi rúbrica"
                    subiendoArchivoPublicState={subiendo}
                    setSubiendoArchivoPublicState={setSubiendo}
                    resetInitialFile={false}
                    showToastSuccess={false}
                    extensionesValidas={["pdf", "doc", "docx", "xls", "xlsx"]}
                />

                <label className="mt-3">Comentarios (opcional)</label>
                <textarea className="form-control" rows="4" value={comentario}
                          onChange={e => setComentario(e.target.value)} disabled={enviando} />

                <Actions>
                    <button type="button"
                            className="generic-button-buscador generic-button-hover-verde green-innova-background"
                            disabled={enviando || subiendo}
                            onClick={enviar}>
                        Enviar
                        {enviando && <FontAwesomeIcon icon="spinner" className="spinner ml-2" />}
                    </button>
                </Actions>
            </Container>
        </GenericModal>
    );
};

export default CompletarClaseModeloModal;

const Container = styled.div`padding: 0 16px 18px;`;
const Divider = styled.div`border-top: 2px solid #0645ff; margin: 18px 0;`;
const SectionTitle = styled.div`display: inline-block; background: #0645ff; color: #fff; padding: 7px 12px; font-weight: 600; margin-bottom: 14px;`;
const Grid = styled.div`display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px 24px; @media (max-width: 760px) { grid-template-columns: 1fr; }`;
const Field = styled.div`label { display: block; font-size: 12px; margin-bottom: 4px; }`;
const ReadOnly = styled.div`background: #f1f1f1; min-height: 32px; padding: 7px 9px;`;
const Actions = styled.div`display: flex; justify-content: flex-end; margin-top: 18px; button { min-width: 150px; }`;
